import { incomePlan } from "@/lib/fers";

export const SPOUSE_DEFAULTS = {
  enabled: false, name: "Spouse", age: 35, birth_year: "", birth_date: "", service_start_date: "", retire_age: 62, salary: 70000, retirement_system: "FERS",
  service_years: 5, high3_override: 0, survivor_benefit: "none", contrib_mode: "percent", contrib_pct: 5, contrib_value: 0, pay_frequency: "biweekly", roth_split: 0, salary_growth: 2.5,
  tsp_balance: 0, trad_ira: 0, roth_ira: 0, ss_pia_override: 0, ss_claim_age: 67, life_expectancy: 92,
};

export const SURVIVOR_DEFAULTS = { enabled: false, who: "auto", death_age: null };

export const spouseOf = (p) => ({ ...SPOUSE_DEFAULTS, ...(p.spouse || {}) });
export const survivorOf = (p) => ({ ...SURVIVOR_DEFAULTS, ...(p.survivor_scenario || {}) });

export function spouseProfile(p) {
  const s = spouseOf(p);
  return {
    ...p, age: Number(s.age), birth_year: Number(s.birth_year) || null, birth_date: s.birth_date || "", service_start_date: s.service_start_date || "",
    retire_age: Number(s.retire_age), life_expectancy: Number(s.life_expectancy),
    salary: Number(s.salary), retirement_system: s.retirement_system, service_years: Number(s.service_years), high3_override: Number(s.high3_override),
    survivor_benefit: s.survivor_benefit, contrib_mode: s.contrib_mode === "fixed" ? "fixed" : "percent", contrib_value: Number(s.contrib_mode === "fixed" ? s.contrib_value : s.contrib_pct), pay_frequency: s.pay_frequency || "biweekly", roth_split: Number(s.roth_split),
    salary_growth: Number(s.salary_growth), step_increase_pct: 0, ss_pia_override: Number(s.ss_pia_override), ss_claim_age: Number(s.ss_claim_age),
    ira_trad_annual: 0, ira_roth_annual: 0,
  };
}

const spouseBalances = (s) => ({ tsp: Number(s.tsp_balance) || 0, trad_ira: Number(s.trad_ira) || 0, roth_ira: Number(s.roth_ira) || 0 });
const KEYS = ["pension", "supplement", "ss", "withdrawal", "rmd", "gross", "taxes", "net", "balance"];
const THIS_YEAR = new Date().getFullYear();
const byYear = (rows) => Object.fromEntries(rows.map((r) => [r.year, r]));
const endYear = (q) => THIS_YEAR + (q.life_expectancy - q.age);

export function deathInfo(p, sp, sc) {
  const who = sc.who === "you" || sc.who === "spouse" ? sc.who : endYear(p) <= endYear(sp) ? "you" : "spouse";
  const d = who === "you" ? p : sp;
  const deathAge = Number(sc.death_age) || d.life_expectancy;
  return { who, deathAge, deathYear: THIS_YEAR + (deathAge - d.age), decAge: d.age };
}

function survivorRows(p, balances, sp, s, info) {
  const youDie = info.who === "you";
  const dec = youDie ? p : sp, sur = youDie ? sp : p;
  const surEnd = endYear(sur);
  const decLong = incomePlan({ ...dec, life_expectancy: Math.max(dec.life_expectancy, dec.age + (surEnd - THIS_YEAR)) }, youDie ? balances : spouseBalances(s));
  const surPlan = incomePlan(sur, youDie ? spouseBalances(s) : balances);
  const D = byYear(decLong.rows), S = byYear(surPlan.rows);
  const atDeath = D[info.deathYear];
  const pen = decLong.pen;
  const colaFactor = atDeath?.pension > 0 && pen.annual > 0 ? atDeath.pension / pen.annual : 0;
  const annuity0 = pen.base * pen.survivorPct * colaFactor;
  const infl = (Number(p.inflation) || 0) / 100, tax = (Number(p.tax_rate) || 0) / 100;
  const decSS0 = atDeath?.ss > 0 ? atDeath.ss : decLong.ss.pia * 12 * Math.pow(1 + infl, Math.max(0, info.deathYear - THIS_YEAR));
  const rows = [];
  for (let y = info.deathYear + 1; y <= surEnd; y++) {
    const own = S[y] || {}, dl = D[y] || {}, k = y - info.deathYear;
    const surAge = sur.age + (y - THIS_YEAR);
    const survivorAnnuity = annuity0 * Math.pow(1 + pen.colaPct / 100, k);
    const decSS = surAge >= 60 ? decSS0 * Math.pow(1 + infl, k) : 0;
    const ss = Math.max(own.ss || 0, decSS);
    const fromPre = (own.fromPre || 0) + (dl.fromPre || 0);
    const withdrawal = (own.withdrawal || 0) + (dl.withdrawal || 0);
    const pension = (own.pension || 0) + survivorAnnuity;
    const gross = pension + (own.supplement || 0) + ss + withdrawal;
    const taxes = (pension + (own.supplement || 0) + fromPre + 0.85 * ss) * tax;
    rows.push({
      year: y, youAge: youDie ? null : surAge, spouseAge: youDie ? surAge : null, youRetired: !youDie && !!S[y], spouseRetired: youDie && !!S[y],
      survivor: true, survivorAnnuity, ssSwitched: decSS > (own.ss || 0), pension, supplement: own.supplement || 0, ss, withdrawal, rmd: (own.rmd || 0) + (dl.rmd || 0),
      gross, taxes, net: gross - taxes, monthly: (gross - taxes) / 12, balance: (own.balance || 0) + (dl.balance || 0),
      youNet: youDie ? 0 : gross - taxes, spouseNet: youDie ? gross - taxes : 0,
    });
  }
  return { rows, annuity0, decSS0, survivorPct: pen.survivorPct, retiredBeforeDeath: colaFactor > 0 };
}

export function householdPlan(p, balances) {
  const you = incomePlan(p, balances);
  const s = spouseOf(p);
  if (!s.enabled) return { you, spouse: null, survivor: null, rows: you.rows.map((r) => ({ ...r, youAge: r.age, spouseAge: null })) };
  const sp = spouseProfile(p);
  const spouse = incomePlan(sp, spouseBalances(s));
  const a = byYear(you.rows), b = byYear(spouse.rows);
  const years = [...new Set([...Object.keys(a), ...Object.keys(b)])].map(Number).sort((x, y) => x - y);
  let rows = years.map((y) => {
    const o = { year: y, youAge: p.age + (y - THIS_YEAR), spouseAge: sp.age + (y - THIS_YEAR), youRetired: !!a[y], spouseRetired: !!b[y] };
    KEYS.forEach((k) => { o[k] = (a[y]?.[k] || 0) + (b[y]?.[k] || 0); });
    o.monthly = o.net / 12;
    o.youNet = a[y]?.net || 0;
    o.spouseNet = b[y]?.net || 0;
    return o;
  });
  const sc = survivorOf(p);
  if (!sc.enabled) return { you, spouse, survivor: null, rows };
  const info = deathInfo(p, sp, sc);
  const sv = survivorRows(p, balances, sp, s, info);
  rows = [...rows.filter((r) => r.year <= info.deathYear), ...sv.rows];
  const before = rows.filter((r) => r.year <= info.deathYear && r.youRetired && r.spouseRetired).pop() || rows.filter((r) => r.year <= info.deathYear).pop();
  const survivorName = info.who === "you" ? s.name || "Spouse" : "You";
  return { you, spouse, rows, survivor: { ...info, ...sv, survivorName, deceasedName: info.who === "you" ? "You" : s.name || "Spouse", before, after: sv.rows[0] || null } };
}
